import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { applySEO } from '@/components/feature/SEOHead';

import { brandInfo, reviews } from '@/mocks/siteData';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d9qq65nnahb7382ovdgg';

export default function About() {
  const [formUrl] = useState<string>(CONTACT_FORM_URL);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    applySEO({
      title: '关于我们 - 笔趣阁 | 全网最速小说更新平台',
      description: '笔趣阁是国内领先的正版小说阅读与更新平台，成立于2018年。平台收录超十万部小说，覆盖玄幻、都市、言情、武侠、科幻等全品类。支持全文无删减阅读，多端无缝切换，自动保存阅读进度。',
      keywords: '笔趣阁,关于笔趣阁,小说阅读平台,笔趣阁简介,在线阅读平台,正版小说,笔趣阁联系',
      canonicalUrl: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/about' : '',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于笔趣阁',
        description: '笔趣阁是国内领先的正版小说阅读与更新平台，成立于2018年，为读者提供最快、最全、最优质的小说阅读体验。',
        url: typeof window !== 'undefined' ? window.location.origin + ((window as any).__BASE_PATH__ || '') + '/about' : '',
        isPartOf: {
          '@type': 'WebSite',
          name: '笔趣阁',
        },
      },
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formUrl) {
      setFormError('表单暂未配置，请稍后再试。');
      setFormStatus('error');
      return;
    }
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypotInput = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotInput && honeypotInput.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      if (honeypotInput) {
        formData.delete('phone_alt');
      }
      const body = new URLSearchParams(formData as any).toString();
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试。';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后再试。');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch (err) {
      setFormError('网络错误，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20">
        {/* Header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-3">
              关于笔趣阁
            </h1>
            <p className="text-foreground-500 text-sm mt-1">{brandInfo.subtitle}</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                  品牌介绍
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  {brandInfo.description}
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  我们始终相信，好的故事值得被完整地呈现。笔趣阁坚持不删减、不修改的运营原则，与数千位作者保持紧密合作，确保每一部作品都能以最完整的形态呈现在读者面前。平台的快速同步技术让读者与作者之间没有时差，最新章节发布的瞬间即可推送到百万读者的设备上。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-80">
                <div className="bg-background-100 rounded-2xl p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                    <i className="ri-quill-pen-line text-primary-500 text-2xl"></i>
                  </div>
                  <p className="font-heading text-xl font-bold text-foreground-900 mb-2">成立于 {brandInfo.foundedYear} 年</p>
                  <p className="text-sm text-foreground-500">专注小说阅读领域<br/>服务数亿次阅读请求</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {brandInfo.stats.map((stat, idx) => (
                <div key={idx} className="text-center p-6 bg-background-50 rounded-lg border border-background-200/70">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">{stat.value}</p>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                为什么选择笔趣阁
              </h2>
              <p className="text-foreground-500 text-sm mt-2">四大核心优势，打造极致阅读体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {brandInfo.features.map((feat, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-6 text-center hover:border-primary-200 border border-transparent transition-colors">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                    <i className={`${feat.icon} text-accent-500 text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feat.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews / Reputation */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                读者口碑
              </h2>
              <p className="text-foreground-500 text-sm mt-2">来自千万读者的真实评价</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-accent-600 font-heading font-bold text-sm">
                        {review.userName.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.userName}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-background-200/70">
                    <span className="text-xs text-foreground-400 flex items-center gap-1">
                      <i className="ri-thumb-up-line"></i> {review.likes.toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm mt-2">如有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <form onSubmit={handleSubmit} data-readdy-form="">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      姓名 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-800"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                      邮箱 <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-800"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    name="subject"
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-800"
                    placeholder="请输入主题"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                    留言内容 <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-300 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all text-foreground-800 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="hp-field"
                />

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {formStatus === 'submitting' ? '提交中...' : '提交留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="mt-4 p-3 bg-secondary-50 border border-secondary-200 rounded-lg text-sm text-secondary-800 text-center">
                    感谢您的留言，我们会尽快回复！
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="mt-4 p-3 bg-accent-50 border border-accent-200 rounded-lg text-sm text-accent-800 text-center">
                    {formError}
                  </div>
                )}
              </form>
            </div>

            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-50 rounded-full">
                  <i className="ri-mail-line text-primary-500"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">电子邮箱</p>
                <p className="text-xs text-foreground-500 mt-1">contact@biquge.com</p>
              </div>
              <div className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-50 rounded-full">
                  <i className="ri-wechat-line text-primary-500"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">微信公众号</p>
                <p className="text-xs text-foreground-500 mt-1">笔趣阁官方</p>
              </div>
              <div className="text-center p-5 bg-background-100 rounded-lg border border-background-200/70">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center bg-primary-50 rounded-full">
                  <i className="ri-customer-service-line text-primary-500"></i>
                </div>
                <p className="text-sm font-medium text-foreground-800">客服时间</p>
                <p className="text-xs text-foreground-500 mt-1">周一至周日 9:00-21:00</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}